"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.reducer = exports.mutations = void 0;
const immer_1 = require("immer");
const meta_state_1 = require("../shared/meta-state");
const constants_1 = require("./constants");
const execute_events_1 = require("./execution/execute-events");
const execute_expressions_1 = require("./execution/execute-expressions");
const iterable_count_1 = require("./execution/iterable-count");
const create_initial_state_1 = require("./init/create-initial-state");
const reference_table_1 = require("./reference-table");
const reference_tables_1 = require("./reference-tables");
const stringify_value_1 = require("./utils/stringify-value");
const initialState = {};
exports.mutations = {
    loadForm(state, { modelKey, model, initialTermValues, envData }) {
        const formMetaState = (0, meta_state_1.convertToFormMetaState)(model);
        const referenceTable = new reference_table_1.ReferenceTable(model, formMetaState, initialTermValues);
        reference_tables_1.referenceTables[modelKey] = referenceTable;
        const { nodeToKey, sortedReactiveExpressions, onReadyEvents, termDependents, } = referenceTable;
        const modelState = (0, create_initial_state_1.createInitialState)(modelKey, model, nodeToKey, formMetaState, initialTermValues, envData);
        (0, execute_expressions_1.executeExpressions)(modelState, sortedReactiveExpressions);
        for (const stateLocation of (0, execute_events_1.executeEvents)(modelState, onReadyEvents, {
            name: "READY",
        })) {
            (0, execute_expressions_1.executeExpressions)(modelState, stateLocation.type === "iterable-term"
                ? termDependents.iterables[stateLocation.iterableName]?.terms[stateLocation.termName]
                : termDependents.terms[stateLocation.termName]);
        }
        modelState.phase = "READY";
        state[modelKey] = modelState;
    },
    updateTermValues(state, { modelKey, termValues }) {
        throwIfNoInstance(modelKey, state);
        const formState = state[modelKey];
        const { keyToNode, termDependents, sortedReactiveExpressions } = reference_tables_1.referenceTables[modelKey];
        const queueExpressions = new Set();
        const queueIterableTerms = new Set();
        for (const [termKey, termValue] of Object.entries(termValues)) {
            // ignore undefined values, those should be treated the same as deleted properties
            if (termValue === undefined) {
                continue;
            }
            throwIfValueNotSerializable(termKey, termValue);
            const node = keyToNode[termKey];
            if (!node) {
                console.warn(`No node found for term key ${termKey}`);
                continue;
            }
            // Handle iterable terms after so that the count is correctly set first
            // This also allows us to keep track of the iterable origin when running expressions
            if (node.type === "iterable-term") {
                queueIterableTerms.add([node, termValue]);
                continue;
            }
            const term = formState.terms[node.termName];
            term.input = termValue;
            term.value = (0, stringify_value_1.stringifyValue)(termValue, term);
            const dependents = termDependents.terms[node.termName];
            if (!dependents) {
                continue;
            }
            for (const exp of dependents) {
                queueExpressions.add(exp);
            }
        }
        for (const exp of sortedReactiveExpressions) {
            if (queueExpressions.has(exp)) {
                (0, execute_expressions_1.executeExpression)(formState, exp);
            }
        }
        const queueExpressionsWithIndexes = new Map();
        for (const [node, termValue] of queueIterableTerms) {
            const term = formState.iterables[node.iterableName].instances[node.iterableIndex]
                ?.terms[node.termName];
            if (!term) {
                continue;
            }
            term.input = termValue;
            term.value = (0, stringify_value_1.stringifyValue)(termValue, term);
            const dependents = termDependents.iterables[node.iterableName]?.terms[node.termName];
            if (!dependents) {
                continue;
            }
            for (const exp of dependents) {
                const indexesOrAll = queueExpressionsWithIndexes.get(exp);
                if (indexesOrAll === "all-instances") {
                    continue;
                }
                else if (exp.iterableName !== node.iterableName) {
                    queueExpressionsWithIndexes.set(exp, "all-instances");
                    continue;
                }
                // if the dependent expression is part of the same iterable then save the index
                if (indexesOrAll) {
                    indexesOrAll.add(node.iterableIndex);
                    continue;
                }
                queueExpressionsWithIndexes.set(exp, new Set([node.iterableIndex]));
            }
        }
        for (const exp of sortedReactiveExpressions) {
            const indexesOrAll = queueExpressionsWithIndexes.get(exp);
            if (indexesOrAll !== undefined) {
                if (indexesOrAll === "all-instances") {
                    (0, execute_expressions_1.executeExpression)(formState, exp);
                    continue;
                }
                for (const iterableIndex of indexesOrAll) {
                    (0, execute_expressions_1.executeExpression)(formState, exp, exp.iterableName && iterableIndex !== -1
                        ? {
                            iterableName: exp.iterableName,
                            iterableIndex,
                        }
                        : undefined);
                }
            }
        }
    },
    updateTermValue(state, { modelKey, termKey, termValue }) {
        throwIfValueNotSerializable(termKey, termValue);
        const formState = state[modelKey];
        const { keyToNode, termDependents } = reference_tables_1.referenceTables[modelKey];
        const node = keyToNode[termKey];
        if (!node) {
            console.warn(`No node found for term key ${termKey}`);
            return;
        }
        const term = node.type === "iterable-term"
            ? formState.iterables[node.iterableName].instances[node.iterableIndex]
                ?.terms[node.termName]
            : formState.terms[node.termName];
        if (!term) {
            console.warn(`No term found for term key ${termKey}`);
            return;
        }
        const newValue = (0, stringify_value_1.stringifyValue)(termValue, term);
        if (newValue === term.value) {
            return;
        }
        term.input = termValue;
        term.value = newValue;
        const dependents = node.type === "iterable-term"
            ? termDependents.iterables[node.iterableName]?.terms[node.termName]
            : termDependents.terms[node.termName];
        if (!dependents) {
            return;
        }
        const origin = node.type === "iterable-term"
            ? {
                iterableName: node.iterableName,
                iterableIndex: node.iterableIndex,
            }
            : undefined;
        for (const exp of dependents) {
            (0, execute_expressions_1.executeExpression)(formState, exp, origin);
        }
    },
    termInput(state, { modelKey, termName, termValue, iterableName, iterableIndex, }) {
        throwIfNoInstance(modelKey, state);
        throwIfValueNotSerializable(termName, termValue);
        if (iterableName && iterableIndex === undefined) {
            throw new Error(`Iterable index is required when updating iterable term ${iterableName}/${termName}`);
        }
        const formState = state[modelKey];
        const { termDependents, onTermInputEvents } = reference_tables_1.referenceTables[modelKey];
        const term = iterableName && iterableIndex !== undefined
            ? formState.iterables[iterableName].instances[iterableIndex]?.terms[termName]
            : formState.terms[termName];
        if (!term) {
            throw new Error(`Term "${iterableName
                ? `${iterableName}[${iterableIndex}].${termName}`
                : termName}" does not exist`);
        }
        if (term.isReadonly) {
            console.warn("Since the term is readonly, the update was prevented in the TERM_INPUT action. This is to prevent an application from making a mistake. If the term needs to be updated even when it's readonly, use UPDATE_TERM_VALUES or UPDATE_TERM_VALUE instead.");
            return;
        }
        const newValue = (0, stringify_value_1.stringifyValue)(termValue, term);
        if (term.value === newValue) {
            return;
        }
        term.input = termValue;
        term.value = newValue;
        const origin = iterableName && iterableIndex !== undefined
            ? {
                iterableName,
                iterableIndex,
            }
            : undefined;
        (0, execute_expressions_1.executeExpressions)(formState, iterableName
            ? termDependents.iterables[iterableName]?.terms[termName]
            : termDependents.terms[termName], origin);
        for (const stateLocation of (0, execute_events_1.executeEvents)(formState, iterableName
            ? onTermInputEvents.iterables[iterableName]?.[termName]
            : onTermInputEvents.terms[termName], {
            name: "INPUT",
        }, origin)) {
            (0, execute_expressions_1.executeExpressions)(formState, stateLocation.type === "iterable-term"
                ? termDependents.iterables[stateLocation.iterableName]?.terms[stateLocation.termName]
                : termDependents.terms[stateLocation.termName], origin);
        }
    },
    iterableCount(state, { modelKey, iterableName, count }) {
        throwIfNoInstance(modelKey, state);
        const formState = state[modelKey];
        const { termDependents, onIterableCountEvents } = reference_tables_1.referenceTables[modelKey];
        const iterable = formState.iterables[iterableName];
        if (!iterable) {
            throw new Error(`Iterable "${iterableName}" does not exist`);
        }
        if (iterable.count === count) {
            return;
        }
        if (count < 0) {
            throw new Error(`Iterable count cannot be less than 0. Received ${count} for iterable ${iterableName}.`);
        }
        if (count > iterable.max) {
            throw new Error(`Iterable count cannot be greater than the max of ${iterable.max}. Received ${count} for iterable ${iterableName}.`);
        }
        for (const stateLocation of (0, execute_events_1.executeEvents)(formState, onIterableCountEvents[iterableName], {
            name: "ITERABLE_ACTION",
            count,
        })) {
            (0, execute_expressions_1.executeExpressions)(formState, 
            // Only terms can be affected by iterable actions
            termDependents.terms[stateLocation.termName]);
        }
    },
    iterableAdd(state, { modelKey, iterableName, index }) {
        throwIfNoInstance(modelKey, state);
        const formState = state[modelKey];
        const { model, formMetaState, nodeToKey, termDependents, onIterableCountEvents, } = reference_tables_1.referenceTables[modelKey];
        const iterable = formState.iterables[iterableName];
        if (!iterable) {
            throw new Error(`Iterable "${iterableName}" does not exist`);
        }
        if (iterable.count >= iterable.max) {
            throw new Error(`Max count reached for iterable "${iterableName}"`);
        }
        const safeIndex = Math.min(iterable.count, Math.max(0, index));
        const instance = (0, create_initial_state_1.createIterableInstance)(model, formMetaState, nodeToKey, iterableName, safeIndex);
        iterable.instances.splice(safeIndex, 0, instance);
        // Run expressions for the new instance and every instance after it
        // This could potentially be optimized by only running expressions that depend on the instance index or position
        (0, iterable_count_1.runAllExpressionsForEachInstance)(formState, model.iterables[iterableName], iterable.instances.map((_, i) => i).slice(safeIndex));
        for (const stateLocation of (0, execute_events_1.executeEvents)(formState, onIterableCountEvents[iterableName], {
            name: "ITERABLE_ACTION",
            count: iterable.instances.length,
        })) {
            (0, execute_expressions_1.executeExpressions)(formState, 
            // Only terms can be affected by iterable actions
            termDependents.terms[stateLocation.termName]);
        }
    },
    iterableRemove(state, { modelKey, iterableName, index }) {
        throwIfNoInstance(modelKey, state);
        const formState = state[modelKey];
        const { model, termDependents, onIterableCountEvents } = reference_tables_1.referenceTables[modelKey];
        const iterable = formState.iterables[iterableName];
        if (!iterable) {
            throw new Error(`Iterable "${iterableName}" does not exist`);
        }
        if (!Number.isInteger(index)) {
            throw new Error(`Iterable index must be an integer. Received ${index} for iterable ${iterableName}.`);
        }
        if (index < 0) {
            throw new Error(`Iterable index cannot be less than 0. Received ${index} for iterable ${iterableName}.`);
        }
        if (index > iterable.count - 1) {
            throw new Error(`Iterable index cannot be greater than the current count of ${iterable.count}. Received ${index} for iterable ${iterableName}.`);
        }
        iterable.instances.splice(index, 1);
        // Run expressions for the new instance and every instance after it
        // This could potentially be optimized by only running expressions that depend on the instance index or position
        (0, iterable_count_1.runAllExpressionsForEachInstance)(formState, model.iterables[iterableName], iterable.instances.map((_, i) => i).slice(index));
        for (const stateLocation of (0, execute_events_1.executeEvents)(formState, onIterableCountEvents[iterableName], {
            name: "ITERABLE_ACTION",
            count: iterable.instances.length,
        })) {
            (0, execute_expressions_1.executeExpressions)(formState, 
            // Only terms can be affected by iterable actions
            termDependents.terms[stateLocation.termName]);
        }
    },
    iterableMove(state, { modelKey, iterableName, fromIndex, toIndex, }) {
        throwIfNoInstance(modelKey, state);
        const formState = state[modelKey];
        const { model } = reference_tables_1.referenceTables[modelKey];
        const iterable = formState.iterables[iterableName];
        if (!iterable) {
            throw new Error(`Iterable "${iterableName}" does not exist`);
        }
        if (fromIndex === toIndex) {
            return;
        }
        if (fromIndex < 0 ||
            fromIndex >= iterable.count ||
            toIndex < 0 ||
            toIndex >= iterable.count) {
            throw new Error(`Iterable from/to index cannot be less than 0 or greater than the current count of ${iterable.count}. Received fromIndex: ${fromIndex}, toIndex: ${toIndex} for iterable ${iterableName}.`);
        }
        const instance = iterable.instances.splice(fromIndex, 1)[0];
        iterable.instances.splice(toIndex, 0, instance);
        // Run expressions for the new instance and every instance after it
        // This could potentially be optimized by only running expressions that depend on the instance index or position
        (0, iterable_count_1.runAllExpressionsForEachInstance)(formState, model.iterables[iterableName], iterable.instances
            .map((_, i) => i)
            .slice(Math.min(fromIndex, toIndex), Math.max(fromIndex, toIndex) + 1));
    },
    dispatchActions(state, { modelKey, actions, args }) {
        throwIfNoInstance(modelKey, state);
        const formState = state[modelKey];
        const { onActionEvents, termDependents } = reference_tables_1.referenceTables[modelKey];
        for (const actionName of actions) {
            for (const stateLocation of (0, execute_events_1.executeEvents)(formState, onActionEvents[actionName], {
                name: "ACTION",
                action: actionName,
                args,
            })) {
                (0, execute_expressions_1.executeExpressions)(formState, stateLocation.type === "iterable-term"
                    ? termDependents.iterables[stateLocation.iterableName]?.terms[stateLocation.termName]
                    : termDependents.terms[stateLocation.termName]);
            }
        }
    },
    removeForm(state, { modelKey }) {
        delete state[modelKey];
        delete reference_tables_1.referenceTables[modelKey];
    },
};
exports.reducer = (0, immer_1.produce)((draft, action) => {
    switch (action.type) {
        case constants_1.LOAD_FORM: {
            exports.mutations.loadForm(draft, action.payload);
            break;
        }
        case constants_1.UPDATE_TERM_VALUES: {
            exports.mutations.updateTermValues(draft, action.payload);
            break;
        }
        case constants_1.UPDATE_TERM_VALUE: {
            exports.mutations.updateTermValue(draft, action.payload);
            break;
        }
        case constants_1.TERM_INPUT: {
            exports.mutations.termInput(draft, action.payload);
            break;
        }
        case constants_1.ITERABLE_COUNT: {
            exports.mutations.iterableCount(draft, action.payload);
            break;
        }
        case constants_1.ITERABLE_ADD: {
            exports.mutations.iterableAdd(draft, action.payload);
            break;
        }
        case constants_1.ITERABLE_REMOVE: {
            exports.mutations.iterableRemove(draft, action.payload);
            break;
        }
        case constants_1.ITERABLE_MOVE: {
            exports.mutations.iterableMove(draft, action.payload);
            break;
        }
        case constants_1.ACTION_DISPATCH: {
            exports.mutations.dispatchActions(draft, action.payload);
            break;
        }
        case constants_1.REMOVE_FORM: {
            exports.mutations.removeForm(draft, action.payload);
            break;
        }
        default:
            break;
    }
}, initialState);
function throwIfNoInstance(modelKey, state) {
    if (!modelKey || !state[modelKey] || !reference_tables_1.referenceTables[modelKey]) {
        throw new Error(`Form instance by this name does not exist: ${modelKey}`);
    }
}
function throwIfValueNotSerializable(name, value) {
    if (typeof value === "string" ||
        (typeof value === "number" && !isNaN(value)) ||
        typeof value === "boolean" ||
        value instanceof Date) {
        return;
    }
    throw new Error(`Value for "${name}"" is not serializable`);
}
//# sourceMappingURL=reducer.js.map